<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-xl bg-gray-800 text-gray-300 rounded-lg shadow-lg border border-gray-700 animate-scaleIn flex flex-col gap-4 relative"
         style="max-height:90vh;">
      <!-- Header -->
      <div class="flex items-center justify-between px-4 pt-4">
        <div class="flex items-center gap-3">
          <img
            :src="data?.img || 'default-item.png'"
            alt="Equipment Image"
            class="w-9 h-9 sm:w-14 sm:h-14 rounded-md object-cover border-2"
            :class="data?.rarityColors?.borderColor || 'border-gray-600'"
            @error="handleImageError"
          />
          <div>
            <h1 class="text-base sm:text-lg font-semibold"
                :class="data?.rarityColors?.textColor || 'text-gray-300'">
              {{ data?.name || 'ไม่ระบุ' }} <span v-if="data?.level">+{{ data?.level || 0 }}</span>
            </h1>
            <p class="text-gray-300 text-xs sm:text-sm">ประเภท: {{ data?.type || 'ไม่ระบุ' }}</p>
            <div v-if="data?.rarity" class="flex items-center gap-1 mt-1">
              <template v-for="star in Number(data.rarity)" :key="star">
                <i class="fas fa-star text-yellow-400 text-xs sm:text-sm"></i>
              </template>
            </div>
          </div>
        </div>
        <button
          @click="$emit('close')"
          class="text-gray-300 hover:text-indigo-300 transition-colors duration-200 group"
          :class="{ 'opacity-50 cursor-not-allowed': isUpgrading }"
          aria-label="ปิด"
          :disabled="isUpgrading"
        >
          <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
        </button>
      </div>

      <!-- Tabs -->
      <div class="px-4">
        <div class="grid grid-cols-2 border-b border-gray-700 bg-gray-800 rounded-t-md">
          <button
            @click="activeTab = 'info'"
            :class="[
              'relative px-3 py-2 text-xs sm:text-sm font-medium transition-colors duration-200',
              activeTab === 'info'
                ? 'text-indigo-300 border-b-2 border-indigo-600'
                : 'text-gray-300 hover:text-indigo-300 hover:bg-gray-900'
            ]"
            :aria-selected="activeTab === 'info'"
            aria-label="ข้อมูล"
          >
            ข้อมูล
            <span v-if="activeTab === 'info'" class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 animate-tabSlide"></span>
          </button>
          <button
            @click="activeTab = 'upgrade'"
            :class="[
              'relative px-3 py-2 text-xs sm:text-sm font-medium transition-colors duration-200',
              activeTab === 'upgrade'
                ? 'text-indigo-300 border-b-2 border-indigo-600'
                : 'text-gray-300 hover:text-indigo-300 hover:bg-gray-900'
            ]"
            :aria-selected="activeTab === 'upgrade'"
            aria-label="อัปเกรด"
            :disabled="remainingUpgrades <= 0"
          >
            อัปเกรด
            <span v-if="activeTab === 'upgrade'" class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 animate-tabSlide"></span>
          </button>
        </div>
      </div>

      <!-- Notification -->
      <div v-if="notificationMessage" class="px-4 -mt-2">
        <div class="text-center text-xs sm:text-sm"
             :class="notificationType === 'success' ? 'text-green-400' : 'text-red-300'">
          {{ notificationMessage }}
        </div>
      </div>

      <!-- Content -->
      <div class="px-4 pb-4 space-y-4 overflow-y-auto scrollbar-thin" style="max-height: calc(90vh - 170px);">
        <!-- Info Tab -->
        <div v-if="activeTab === 'info'" class="space-y-4 animate-fadeIn">
          <section class="bg-gray-800 border border-gray-700 rounded-md p-3">
            <h2 class="text-sm font-semibold text-gray-100 mb-2">สถานะปัจจุบัน</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div v-for="key in principalStatsKeys" :key="key" class="flex items-center gap-2">
                <i :class="['text-indigo-400 text-sm', statIcons[key]]"></i>
                <span class="text-gray-300">
                  {{ key.charAt(0).toUpperCase() + key.slice(1) }}:
                  <strong class="text-gray-100">{{ calculatedStats[key] }}</strong>
                </span>
              </div>
            </div>
          </section>
          <section class="bg-gray-800 border border-gray-700 rounded-md p-3">
            <h2 class="text-sm font-semibold text-gray-100 mb-2">สถานะการอัปเกรด</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm text-gray-300">
              <div>ระดับปัจจุบัน: <strong class="text-gray-100">{{ currentLevel }}</strong></div>
              <div>อัปเกรดเหลือ: <strong class="text-gray-100">{{ remainingUpgrades }}</strong> ครั้ง</div>
              <div>ระดับสูงสุดตาม Rarity: <strong class="text-gray-100">{{ maxAllowedLevel }}</strong></div>
            </div>
          </section>
        </div>

        <!-- Upgrade Tab -->
        <div v-if="activeTab === 'upgrade'" class="space-y-4 animate-fadeIn">
          <div v-if="remainingUpgrades <= 0 || currentLevel >= maxAllowedLevel" class="text-center text-yellow-400 py-4 text-xs sm:text-sm">
            ถึงขีดจำกัดการอัปเกรดแล้ว (ตรวจสอบ Rarity)
          </div>
          <div v-else class="space-y-4">
            <section class="bg-gray-800 border border-gray-700 rounded-md p-3">
              <h2 class="text-sm font-semibold text-gray-100 mb-2">การอัปเกรดจะเพิ่ม</h2>
              <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div v-for="(value, stat) in incrementStats" :key="stat" class="flex items-center gap-2">
                  <i :class="['text-indigo-400 text-sm', statIcons[stat]]"></i>
                  <span class="text-gray-300 capitalize">{{ stat }}: <strong class="text-gray-100">+{{ value }}</strong></span>
                </div>
              </div>
            </section>
            <section class="bg-gray-800 border border-gray-700 rounded-md p-3">
              <h2 class="text-sm font-semibold text-gray-100 mb-2">ตัวอย่างสถานะหลังอัปเกรด (ถ้าสำเร็จ)</h2>
              <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
                <div v-for="key in principalStatsKeys" :key="key" class="flex items-center gap-2">
                  <i :class="['text-indigo-400 text-sm', statIcons[key]]"></i>
                  <span class="text-gray-300">
                    {{ key.charAt(0).toUpperCase() + key.slice(1) }}:
                    <strong class="text-gray-100">{{ projectedStats[key] }}</strong>
                  </span>
                </div>
              </div>
            </section>
            <section v-for="material in currentMaterials" :key="material.itemName" class="bg-gray-800 border border-gray-700 rounded-md p-3">
              <div class="flex items-center justify-between mb-2">
                <h3 class="text-sm font-semibold text-gray-100">
                  เลือก{{ material.itemName }} ({{ getSelectedCount(material.itemName) }} / {{ material.quantity }})
                </h3>
                <button
                  v-if="material.itemName !== data?.name"
                  @click="autoSelect(material.itemName)"
                  class="bg-indigo-600 hover:bg-indigo-700 text-white text-xs py-1 px-2 rounded-md transition-colors"
                >
                  เลือกอัตโนมัติ
                </button>
              </div>
              <div class="space-y-2 max-h-32 overflow-y-auto scrollbar-thin">
                <div
                  v-for="invItem in availableMaterials(material.itemName)"
                  :key="invItem.id"
                  @click="!isDuplicateMismatch(invItem) && toggleSelection(material.itemName, invItem.id)"
                  class="flex items-center gap-2 bg-gray-700 p-2 rounded-md transition-colors"
                  :class="{
                    'border-indigo-500 bg-indigo-900': selectedMaterials[material.itemName].includes(invItem.id),
                    'opacity-50 cursor-not-allowed': isDuplicateMismatch(invItem),
                    'cursor-pointer hover:bg-gray-600': !isDuplicateMismatch(invItem)
                  }"
                >
                  <img
                    :src="invItem.img || 'default-item.png'"
                    class="w-6 h-6 rounded object-cover border"
                    :class="invItem.rarityColors?.borderColor || 'border-gray-600'"
                    @error="handleImageError"
                  />
                  <span class="text-gray-300 flex-1">
                    {{ invItem.name }} <span v-if="invItem.level">+{{ invItem.level }}</span>
                    <span v-if="invItem.name === data?.name && invItem.level" class="text-green-400 text-xs ml-2">
                      (+{{ Math.min(invItem.level * 5, 25) }}% โอกาสสำเร็จ)
                    </span>
                  </span>
                  <i
                    v-if="invItem.name === data?.name && invItem.transfer === data?.transfer"
                    class="fas fa-check text-green-400 text-xs"
                    title="สามารถใช้อัปเกรดได้"
                  ></i>
                  <i
                    v-else-if="invItem.name === data?.name && data?.transfer === true && invItem.transfer === false"
                    class="fas fa-times text-red-400 text-xs"
                    title="transfer ไม่ตรงกับไอเทมที่อัปเกรด"
                  ></i>
                  <i
                    v-else-if="invItem.name === data?.name && data?.transfer === false && invItem.transfer === true"
                    class="fas fa-exchange-alt text-blue-400 text-xs"
                    title="สามารถโอนได้ แต่ใช้อัปเกรดไม่ได้"
                  ></i>
                  <input
                    type="checkbox"
                    :checked="selectedMaterials[material.itemName].includes(invItem.id)"
                    class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-600 rounded pointer-events-none"
                  />
                </div>
              </div>
            </section>
            <section class="bg-gray-800 border border-gray-700 rounded-md p-3">
              <h2 class="text-sm font-semibold text-gray-100 mb-2">ระบบการันตี (สิทธิ์สะสม: {{ pityCount }})</h2>
              <div class="text-xs sm:text-sm text-gray-300">
                <p>ใช้สิทธิ์: 
                  <select v-model="pitiesToUse" class="bg-gray-700 text-gray-300 border border-gray-600 rounded-md px-2 py-1">
                    <option v-for="n in (maxUsablePities + 1)" :key="n-1" :value="n-1">{{ n-1 }}</option>
                  </select>
                </p>
                <p>โบนัสจากสิทธิ์: <strong class="text-green-400">+{{ pitiesToUse * 10 }}%</strong></p>
              </div>
            </section>
            <section class="bg-gray-800 border border-gray-700 rounded-md p-3">
              <h2 class="text-sm font-semibold text-gray-100 mb-2">โอกาสสำเร็จ</h2>
              <div class="text-xs sm:text-sm text-gray-300">
                <p>ระดับ {{ currentLevel }} → {{ currentLevel + 1 }}</p>
                <p>โอกาสพื้นฐาน: <strong class="text-gray-100">{{ baseChance }}%</strong></p>
                <p v-if="chanceBonus > 0">
                  โบนัสจากวัสดุ (ระดับ +{{ materialLevel }}): <strong class="text-green-400">+{{ chanceBonus }}%</strong>
                </p>
                <p v-if="pitiesToUse > 0">
                  โบนัสจากสิทธิ์: <strong class="text-green-400">+{{ pitiesToUse * 10 }}%</strong>
                </p>
                <p>โอกาสรวม: <strong class="text-gray-100">{{ currentChance }}%</strong></p>
              </div>
            </section>
            <section
              class="bg-gray-800 border border-gray-700 rounded-md p-3"
            >
              <h2 class="text-sm font-semibold text-gray-100 mb-2">เงื่อนไขวัสดุซ้ำชื่อ</h2>
              <div class="text-xs sm:text-sm text-gray-300 space-y-1">
                <p v-if="data?.transfer === true">
                  ไอเทมนี้ <strong class="text-green-400">สามารถโอนได้</strong> ต้องเลือก <strong>{{ data?.name }}</strong> ที่สามารถโอนได้เท่านั้น
                </p>
                <p v-else>
                  ไอเทมนี้ <strong class="text-gray-400">โอนไม่ได้</strong> ต้องเลือก <strong>{{ data?.name }}</strong> ที่โอนไม่ได้เท่านั้น
                </p>
                <p v-if="duplicateTransferMismatch" class="text-red-400 font-semibold mt-1">
                  <i class="fas fa-exclamation-triangle mr-1"></i>
                  วัสดุซ้ำชื่อที่เลือกมีสถานะ transfer ไม่ตรงกับไอเทมนี้
                </p>
                <p v-else-if="getSelectedCount(data?.name) > 0" class="text-green-400 mt-1">
                  <i class="fas fa-check-circle mr-1"></i>
                  สถานะ transfer ของวัสดุซ้ำชื่อที่เลือกตรงกัน
                </p>
              </div>
            </section>
            <section>
              <div v-if="isUpgrading" class="space-y-2">
                <div class="bg-gray-800 h-4 rounded overflow-hidden relative border border-gray-700">
                  <div
                    ref="progressBar"
                    :style="{ width: progress + '%' }"
                    class="h-4 transition-all duration-300 ease-out bg-gradient-to-r from-indigo-600 to-indigo-400 animate-pulse-glow"
                  ></div>
                  <div class="absolute inset-0 flex items-center justify-center text-xs text-white font-semibold">
                    {{ suspenseText }}
                  </div>
                </div>
                <p class="text-center text-gray-300 text-xs animate-pulse">กำลังอัปเกรด... ลุ้นผลลัพธ์!</p>
              </div>
              <button
                v-else
                @click="upgradeEquipment"
                class="w-full bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm py-2 rounded-md transition-colors duration-200 border border-purple-700 flex items-center justify-center gap-2"
                :disabled="!hasEnoughMaterials || currentChance > 80 || duplicateTransferMismatch"
              >
                <i class="fas fa-arrow-up text-xs"></i>
                <span>อัปเกรด</span>
              </button>
            </section>
          </div>
        </div>
      </div>

      <!-- Footer (Close) -->
      <div class="px-4 pb-4">
        <button
          @click="$emit('close')"
          class="w-full bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm flex items-center justify-center gap-2"
          :class="{ 'opacity-50 cursor-not-allowed': isUpgrading }"
          :disabled="isUpgrading"
        >
          <i class="fas fa-times text-xs"></i> ปิด
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { projectFunctions, projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';

export default {
  name: 'EquipmentUpgradeModal',
  props: {
    itemId: {
      type: String,
      required: true,
    },
    inventoryItems: {
      type: Array,
      required: true,
    },
  },
  emits: ['close', 'equipment-upgraded'],
  setup(props, { emit }) {
    const { user } = getUser();
    const activeTab = ref('info');
    const isUpgrading = ref(false);
    const notificationMessage = ref('');
    const notificationType = ref('success');
    const data = ref({});
    const progress = ref(0);
    const progressBar = ref(null);
    const suspenseText = ref('กำลังร้อนเหล็ก...');
    const selectedMaterials = ref({});
    const localInventory = ref([]);
    const pityCount = ref(0);
    const pitiesToUse = ref(0);
    let unsubscribeItem = null;
    let unsubscribePity = null;

    const statIcons = {
      stamina: 'fas fa-heart',
      atk: 'fas fa-dumbbell',
      defense: 'fas fa-shield-alt',
      agi: 'fas fa-wind',
    };

    const principalStatsKeys = computed(() => {
      const hasAgi = (data.value.stats?.agi || 0) > 0 ||
        (data.value.upgradeStats?.agi || 0) > 0 ||
        currentLevel.value >= 5;
      if (atkGroup.includes(data.value.type)) {
        return hasAgi ? ['atk', 'agi'] : ['atk'];
      } else {
        return hasAgi ? ['stamina', 'defense', 'agi'] : ['stamina', 'defense'];
      }
    });

    const atkGroup = ['แว่นตา', 'ปลอกแขน', 'ถุงมือ', 'สนับแข้ง', 'สร้อยคอ', 'แหวน', 'อาวุธ', 'ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์'];

    const incrementStats = computed(() => {
      const currentLevel = data.value.level || 0;
      const agiIncrements = [0, 0, 0, 0, 0, 3, 6, 9];
      const agi = agiIncrements[currentLevel];
      if (atkGroup.includes(data.value.type)) {
        const atkIncrements = [3, 6, 9, 12, 15, 18, 21, 24];
        const result = { atk: atkIncrements[currentLevel] };
        if (agi > 0) result.agi = agi;
        return result;
      } else {
        const defIncrements = [1, 2, 3, 4, 5, 6, 7, 8];
        const increment = defIncrements[currentLevel];
        const result = { stamina: increment, defense: increment };
        if (agi > 0) result.agi = agi;
        return result;
      }
    });

    const remainingUpgrades = computed(() => 8 - currentLevel.value);

    const currentLevel = computed(() => data.value.level || 0);

    const maxAllowedLevel = computed(() => {
      const rarity = Number(data.value.rarity) || 0;
      if (rarity >= 10) return 8;
      if (rarity >= 9) return 3;
      if (rarity >= 8) return 2;
      if (rarity >= 7) return 1;
      return 0;
    });

    const materialLevel = computed(() => {
      const materialName = data.value.name;
      const selectedIds = selectedMaterials.value[materialName] || [];
      if (selectedIds.length === 0) return 0;
      const selectedItem = localInventory.value.find(item => item.id === selectedIds[0]);
      return selectedItem ? (selectedItem.level || 0) : 0;
    });

    const baseChance = computed(() => {
      // โอกาสสำเร็จพื้นฐานตามระดับปัจจุบัน 0→1 ... 7→8
      const chances = [60, 50, 40, 40, 40, 30, 20, 10];
      return chances[currentLevel.value] || 0;
    });

    const chanceBonus = computed(() => {
      return Math.min(materialLevel.value * 5, 25);
    });

    const pityBonus = computed(() => {
      return pitiesToUse.value * 10;
    });

    const maxUsablePities = computed(() => {
      const maxFromBonus = Math.floor((80 - baseChance.value - chanceBonus.value) / 10);
      return Math.min(Math.max(0, maxFromBonus), pityCount.value);
    });

    const currentChance = computed(() => {
      return baseChance.value + chanceBonus.value + pityBonus.value;
    });

    const currentMaterials = computed(() => {
      // ทุกระดับต้องใช้ไอเทมซ้ำชื่อ 1 ชิ้น และวัสดูอื่นตามระดับ
      if (currentLevel.value >= 5) {
        return [
          { itemName: data.value.name, quantity: 1 },
          { itemName: 'แผ่นเหล็กกล้า', quantity: 1 },
          { itemName: 'แผ่นเคฟลาร์', quantity: currentLevel.value - 4 },
        ];
      }
      return [
        { itemName: data.value.name, quantity: 1 },
        { itemName: 'เหล็กกล้า', quantity: 3 * (currentLevel.value + 1) },
      ];
    });

    const calculatedStats = computed(() => {
      const baseStats = data.value.stats || {};
      const upgradeStats = data.value.upgradeStats || {};
      if (atkGroup.includes(data.value.type)) {
        return {
          atk: (baseStats.atk || 0) + (upgradeStats.atk || 0),
          agi: (baseStats.agi || 0) + (upgradeStats.agi || 0),
        };
      } else {
        return {
          stamina: (baseStats.stamina || 0) + (upgradeStats.stamina || 0),
          defense: (baseStats.defense || 0) + (upgradeStats.defense || 0),
          agi: (baseStats.agi || 0) + (upgradeStats.agi || 0),
        };
      }
    });

    const projectedStats = computed(() => {
      const current = calculatedStats.value;
      const increment = incrementStats.value;
      const projected = {};
      for (const key in current) {
        projected[key] = current[key] + (increment[key] || 0);
      }
      return projected;
    });

    const availableMaterials = computed(() => (itemName) => {
      return localInventory.value
        .filter(invItem => invItem.name === itemName && (itemName !== data.value.name || invItem.id !== props.itemId))
        .sort((a, b) => (a.level || 0) - (b.level || 0));
    });

    const getSelectedCount = (itemName) => selectedMaterials.value[itemName]?.length || 0;

    const hasEnoughMaterials = computed(() => {
      return currentMaterials.value.every(material => getSelectedCount(material.itemName) >= material.quantity);
    });

    // กฎ: ไอเทมวัสดุที่เป็นชื่อเดียวกันกับไอเทมที่กำลังอัปเกรด (duplicate item)
    // ต้องมี states.transfer เหมือนกับไอเทมที่กำลังอัปเกรด
    // วัสดูอื่น เช่น เหล็กกล้า แผ่นเหล็กกล้า แผ่นเคฟลาร์ ไม่ต้องสนใจสถานะ transfer
    const duplicateTransferMismatch = computed(() => {
      // ไอเทมวัสดุซ้ำชื่อต้องมี transfer เหมือนกับไอเทมที่กำลังอัปเกรดทุกระดับ
      const selectedIds = selectedMaterials.value[data.value.name] || [];
      if (selectedIds.length === 0) return false;
      const selectedItem = localInventory.value.find(inv => inv.id === selectedIds[0]);
      if (!selectedItem) return false;
      return selectedItem.transfer !== data.value.transfer;
    });

    // ตรวจสอบว่าไอเทมซ้ำชื่อรายการนี้มี transfer ไม่ตรงกับไอเทมที่กำลังอัปเกรดหรือไม่
    const isDuplicateMismatch = (invItem) => {
      return invItem.name === data.value.name && invItem.transfer !== data.value.transfer;
    };

    const toggleSelection = (itemName, id) => {
      if (!selectedMaterials.value[itemName]) {
        selectedMaterials.value[itemName] = [];
      }
      const arr = selectedMaterials.value[itemName];
      const index = arr.indexOf(id);
      if (index > -1) {
        arr.splice(index, 1);
      } else {
        const quantity = currentMaterials.value.find(m => m.itemName === itemName).quantity;
        if (arr.length < quantity) {
          arr.push(id);
        }
      }
    };

    const autoSelect = (itemName) => {
      const material = currentMaterials.value.find(m => m.itemName === itemName);
      if (!material) return;
      let avail = availableMaterials.value(itemName);
      if (itemName === data.value.name) {
        avail = [...avail].sort((a, b) => (b.level || 0) - (a.level || 0));
      }
      const toSelect = avail.slice(0, material.quantity).map(item => item.id);
      selectedMaterials.value[itemName] = toSelect;
    };

    const handleImageError = (event) => {
      event.target.src = 'default-item.png';
    };

    const showNotification = (message, type = 'success') => {
      notificationMessage.value = message;
      notificationType.value = type;
      setTimeout(() => {
        notificationMessage.value = '';
      }, 3000);
    };

    const animateProgress = () => {
      return new Promise(resolve => {
        let increment = 0;
        let speed = 10;
        const suspenseMessages = [
          'กำลังร้อนเหล็ก...',
          'ตีขึ้นรูปอาวุธ...',
          'หลอมรวมพลัง...',
          'เกือบเสร็จแล้ว...',
          'ลุ้นผลลัพธ์!',
        ];
        let messageIndex = 0;

        const interval = setInterval(() => {
          if (Math.random() < 0.2 && increment > 20 && increment < 80) {
            speed = 0;
            suspenseText.value = suspenseMessages[Math.min(messageIndex, suspenseMessages.length - 1)];
            setTimeout(() => {
              speed = Math.random() * 15 + 5;
            }, 500);
          } else {
            increment += speed;
            speed = Math.max(5, speed * 0.95);
            progress.value = Math.min(increment, 100);
            suspenseText.value = suspenseMessages[Math.min(messageIndex, suspenseMessages.length - 1)];
          }

          if (increment >= 100) {
            clearInterval(interval);
            suspenseText.value = 'เสร็จสิ้น!';
            resolve();
          }

          messageIndex = Math.floor(increment / 25);
        }, 100);
      });
    };

    const upgradeEquipment = async () => {
      if (!hasEnoughMaterials.value) return;
      if (currentLevel.value >= maxAllowedLevel.value) {
        showNotification('Rarity ไม่เพียงพอสำหรับการอัปเกรดนี้', 'error');
        return;
      }
      if (currentChance.value > 80) {
        showNotification('โอกาสรวมเกิน 80% ไม่สามารถอัปเกรดได้', 'error');
        return;
      }
      isUpgrading.value = true;
      progress.value = 0;
      suspenseText.value = 'กำลังร้อนเหล็ก...';

      try {
        const materialItemIds = Object.values(selectedMaterials.value).flat();
        await new Promise(resolve => setTimeout(resolve, 2000));
        const animationPromise = animateProgress();
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({
          action: 'upgradeItem',
          instanceId: props.itemId,
          materialInstanceIds: materialItemIds,
          pitiesToUse: pitiesToUse.value,
        });
        const { isSuccess, message, updatedItem } = result.data;
        await animationPromise;

        if (progressBar.value) {
          progressBar.value.classList.remove('bg-gradient-to-r', 'from-indigo-600', 'to-indigo-400', 'animate-pulse-glow');
          progressBar.value.classList.add(isSuccess ? 'bg-green-600' : 'bg-red-600');
          if (!isSuccess) {
            progressBar.value.classList.add('animate-shake');
          }
        }

        await new Promise(resolve => setTimeout(resolve, 1000));

        if (isSuccess) {
          showNotification('อัปเกรดสำเร็จ!', 'success');
          if (updatedItem) {
            data.value = { ...data.value, ...updatedItem };
          }
          emit('equipment-upgraded', updatedItem);
          localInventory.value = localInventory.value.filter(item => !materialItemIds.includes(item.id));
          selectedMaterials.value = {};
          currentMaterials.value.forEach(material => {
            selectedMaterials.value[material.itemName] = [];
          });
          pitiesToUse.value = 0;
          // Do not update pityCount locally; rely on Firestore snapshot
        } else {
          showNotification(message || 'อัปเกรดล้มเหลว', 'error');
          localInventory.value = localInventory.value.filter(item => !materialItemIds.includes(item.id));
          selectedMaterials.value = {};
          currentMaterials.value.forEach(material => {
            selectedMaterials.value[material.itemName] = [];
          });
          pitiesToUse.value = 0;
          // Do not update pityCount locally; rely on Firestore snapshot
        }
      } catch (err) {
        console.error('Error upgrading equipment:', err);
        showNotification('เกิดข้อผิดพลาดในการอัปเกรด', 'error');
      } finally {
        isUpgrading.value = false;
        if (progressBar.value) {
          progressBar.value.classList.remove('animate-shake');
        }
      }
    };

    watch([chanceBonus, pityCount], () => {
      if (pitiesToUse.value > maxUsablePities.value) {
        pitiesToUse.value = maxUsablePities.value;
      }
      if (pitiesToUse.value > pityCount.value) {
        pitiesToUse.value = pityCount.value;
      }
    });

    const subscribeToPity = () => {
      const pityDocId = `${user.value.uid}_${data.value.name}_${currentLevel.value}`;
      unsubscribePity = projectFirestore.collection('upgradePities').doc(pityDocId).onSnapshot(
        (pitySnap) => {
          pityCount.value = pitySnap.exists ? (pitySnap.data().pityCount || 0) : 0;
        },
        (error) => {
          console.error('Error fetching pity count:', error);
          pityCount.value = 0;
        }
      );
    };

    onMounted(() => {
      localInventory.value = [...props.inventoryItems];
      const found = props.inventoryItems.find(item => item.id === props.itemId || item.instanceId === props.itemId);
      if (found) {
        data.value = found;
        currentMaterials.value.forEach(material => {
          if (!selectedMaterials.value[material.itemName]) {
            selectedMaterials.value[material.itemName] = [];
          }
        });
        if (unsubscribePity) {
          unsubscribePity();
          unsubscribePity = null;
        }
        subscribeToPity();
      } else {
        showNotification('ไม่พบไอเทม', 'error');
        emit('close');
      }
    });

    // Watch for prop inventoryItems changes (e.g. Firestore onSnapshot updates)
    watch(() => props.inventoryItems, (newItems) => {
      if (!newItems || !Array.isArray(newItems)) return;
      localInventory.value = [...newItems];
      const found = newItems.find(item => item.id === props.itemId || item.instanceId === props.itemId);
      if (found) {
        data.value = found;
      }
    }, { deep: true });

    onUnmounted(() => {
      if (unsubscribeItem) {
        unsubscribeItem();
        unsubscribeItem = null;
      }
      if (unsubscribePity) {
        unsubscribePity();
        unsubscribePity = null;
      }
    });

    return {
      activeTab,
      isUpgrading,
      notificationMessage,
      notificationType,
      data,
      principalStatsKeys,
      statIcons,
      incrementStats,
      remainingUpgrades,
      currentLevel,
      maxAllowedLevel,
      baseChance,
      chanceBonus,
      pityBonus,
      materialLevel,
      currentChance,
      currentMaterials,
      calculatedStats,
      projectedStats,
      availableMaterials,
      selectedMaterials,
      getSelectedCount,
      hasEnoughMaterials,
      duplicateTransferMismatch,
      isDuplicateMismatch,
      toggleSelection,
      autoSelect,
      handleImageError,
      showNotification,
      upgradeEquipment,
      progress,
      progressBar,
      suspenseText,
      pityCount,
      pitiesToUse,
      maxUsablePities,
    };
  },
};
</script>

<style scoped>
/* Custom Scrollbar */
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

/* Animations */
.animate-pulse {
  animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.animate-shake {
  animation: shake 0.5s;
}

@keyframes shake {
  0% { transform: translateX(0); }
  10% { transform: translateX(-10px); }
  20% { transform: translateX(10px); }
  30% { transform: translateX(-10px); }
  40% { transform: translateX(10px); }
  50% { transform: translateX(-10px); }
  60% { transform: translateX(10px); }
  70% { transform: translateX(-10px); }
  80% { transform: translateX(10px); }
  90% { transform: translateX(-10px); }
  100% { transform: translateX(0); }
}

@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}

@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

@keyframes pulse-glow {
  0% { box-shadow: 0 0 5px rgba(99, 102, 241, 0.5); }
  50% { box-shadow: 0 0 15px rgba(99, 102, 241, 0.8); }
  100% { box-shadow: 0 0 5px rgba(99, 102, 241, 0.5); }
}
.animate-pulse-glow {
  animation: pulse-glow 1.5s infinite;
}

@keyframes tabSlide {
  from { width: 0; }
  to { width: 100%; }
}
.animate-tabSlide {
  animation: tabSlide 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn {
  animation: fadeIn 0.3s ease-out;
}

button:not(:disabled):active {
  transform: scale(0.98);
}
</style>